import { Component, DOCUMENT, Inject, inject, Renderer2, Signal, signal } from '@angular/core';
import { Navbar } from '../navbar/navbar';
import { Footer } from '../footer/footer';
import { Api } from '../../services/api';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { catchError, of, tap, timeout } from 'rxjs';
import { CareerItems } from '../../interface/careers';
import { NgClass, TitleCasePipe } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Meta, Title } from '@angular/platform-browser';
import { Canonical } from '../../services/canonical';
import { NgxTurnstileComponent, NgxTurnstileFormsModule } from 'ngx-turnstile';
import { environment } from '../../../environments/environment.development';
import { TruncatePipe } from '../../pipes/truncate-pipe';

@Component({
  selector: 'app-career',
  imports: [
    Navbar,
    Footer,
    NgClass,
    ReactiveFormsModule,
    FormsModule,
    TitleCasePipe,
    NgxTurnstileComponent,
    NgxSkeletonLoaderModule,
    NgxTurnstileFormsModule,
    RouterLink,
    TruncatePipe
  ],
  templateUrl: './career.html',
  styleUrl: './career.css',
})
export class Career {
  siteKey = environment.turnstileSiteKey;
  activatedRoute = inject(ActivatedRoute);
  title = inject(Title);
  meta = inject(Meta);
  isLoading = signal(false);
  isApplying = signal(false);
  isSent = signal(false);
  career = signal<CareerItems | undefined>(undefined);
  findBy: string[] = ['Select Option', 'Ads', 'Website', 'Tiktok', 'Facebook', 'Others'];

  private api = inject(Api);
  private canonical = inject(Canonical);

  form = new FormGroup({
    firstname: new FormControl('', Validators.required),
    lastname: new FormControl('', Validators.required),
    email: new FormControl('', [
      Validators.required,
      Validators.email
    ]),
    phone: new FormControl('', [
      Validators.required,
      Validators.pattern(/^\d+$/),
      Validators.minLength(10),
      Validators.maxLength(11)
    ]),
    findBy: new FormControl(this.findBy[0], Validators.required),
    slug: new FormControl('', Validators.required),
    cvOrResume: new FormControl('', Validators.required),
    captchaToken: new FormControl('', Validators.required),
    agree: new FormControl(false, [
      Validators.required,
      Validators.requiredTrue
    ])
  });

  cvOrResume = signal<File | null | undefined>(undefined);

  constructor(
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {}

  capitalize(obj?: string) : string | null {
    if(!obj) return null;

      const word = obj
      .toLowerCase()
      .replace(/[^a-zA-Z0-9 ]/g,' ')
      .split(' ')
      
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

      const string = word.replace(/\s+/g,' ');
      return string.trim();
  }

  selectFile(event: Event) : void {
    const el = event.target as HTMLInputElement;
    const fileList: FileList | null = el.files;

    if (fileList && fileList.length > 0) {
      this.cvOrResume.set(fileList[0]);
    }
  }

  captchaResponse(token: string | null) :void {
    this.form.patchValue({
      captchaToken: token
    });
  }
  
  agree(event: Event) : void {
    const element = event.target as HTMLInputElement;
    const isChecked: boolean = element.checked;
    
    this.form.patchValue({
      agree: isChecked
    });
  }
  
  apply() : void {
    if(this.form.valid){
      this.isApplying.set(true);
      this.isSent.set(false);

      const formData = new FormData();
      formData.append('firstname', this.form.value?.firstname!);
      formData.append('lastname', this.form.value?.lastname!);
      formData.append('email', this.form.value?.email!);
      formData.append('phone', this.form.value?.phone!);
      formData.append('slug', this.form.value?.slug!);
      formData.append('captchaToken', this.form.value?.captchaToken!);
      formData.append('findBy', this.form.value?.findBy!);
      if(this.cvOrResume()){
        const file = this.cvOrResume();
        formData.append('cvOrResume', file!, file?.name);
      }
      
      this.api.applyCareer(formData)
      .pipe(
        timeout(10000),
        catchError(() => of(undefined)),
        tap(() => this.isApplying.set(false))
      ).subscribe(() => {
        this.isSent.set(true);
        this.form.patchValue({
          firstname: '',
          lastname: '',
          email: '',
          phone: '',
          cvOrResume: '',
          captchaToken: '',
          findBy: '',
          agree: false
        });

        this.form.get('firstname')?.markAsUntouched();
        this.form.get('lastname')?.markAsUntouched();
        this.form.get('email')?.markAsUntouched();
        this.form.get('phone')?.markAsUntouched();
        
        this.isApplying.set(false)
      });
    }
  }

  private setMetaTags(obj?: CareerItems) : void {
    this.title.setTitle(obj?.setTitle!);
    this.canonical.setCanonicalUrl(obj?.canonical!);
    this.meta.addTags(JSON.parse(obj?.meta!));
  }

  private setSchemaStructuredData(obj?: CareerItems) : void {
    const script = this.renderer.createElement('script');
    script.type = 'application/ld+json';
    script.text = obj?.schema!;
    this.renderer.appendChild(this.document.head, script);
  }

  private initialize(param?: string | null) : void {
    this.isLoading.set(true);
 
    this.form.patchValue({
      slug: param
    });

    this.api.getCareer(param!)
    .pipe(
      timeout(10000),
      catchError(() => of(undefined)),
      tap(() => this.isLoading.set(false))
    ).subscribe(career =>  {
      this.setMetaTags(career);
      this.setSchemaStructuredData(career);
      this.career.set(career);
    });
  }

  ngOnInit() : void {
    this.activatedRoute.paramMap.subscribe(param => {
      this.initialize(param.get('slug'));
    });
  }
}
